/**
 * The controlled-playtest backoffice (2026-09-06): passes and what the admin sees.
 *
 * A pass is a one-time token the founder issues and a teammate hands out; a
 * session on a gated pod is opened only against an unused pass, which the opening
 * consumes. Cost control is the point: ten passes are ten sessions, no more, and
 * each session's spend is on the admin page beside the pass that paid for it.
 */
import { z } from 'zod';
import { SessionState, VoiceSetting } from './session.js';
/** `EW-XXXX-XXXX`, Crockford base32: no I, L, O or U, typable over a call. */
export const PassTokenString = z.string().regex(/^EW-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/);
export const Pass = z.object({
    token: PassTokenString,
    note: z.string().max(200),
    createdAt: z.string().datetime(),
    usedAt: z.string().datetime().optional(),
    usedBySessionId: z.string().optional(),
    revokedAt: z.string().datetime().optional(),
});
export const PassStatus = z.enum(['unused', 'used', 'revoked']);
export function passStatus(pass) {
    if (pass.revokedAt)
        return 'revoked';
    if (pass.usedAt)
        return 'used';
    return 'unused';
}
export const IssuePassesBody = z.object({
    count: z.number().int().min(1).max(100),
    note: z.string().max(200).default(''),
});
export const PassCheck = z.object({
    ok: z.boolean(),
    reason: z.enum(['unused', 'used', 'revoked', 'unknown', 'malformed', 'not_required', 'spent']),
});
/** ADR-114: what one pass may spend across every session it opens, at the rate card (§8.8). */
export const PASS_BUDGET_USD = 2;
/**
 * ADR-114: a pass is a table, not a ticket. What the pod knows of the table a pass sits
 * at: whether it may still play, what it has spent, the session it has not finished (the
 * client joins that one instead of opening another), and the key its campaign progress is
 * kept under — the same on any browser the pass is typed into.
 */
export const PassTable = z.object({
    ok: z.boolean(),
    reason: z.enum(['open', 'spent', 'revoked', 'unknown', 'malformed', 'not_required']),
    token: PassTokenString.optional(),
    budget: z.object({ spentUsd: z.number().nonnegative(), capUsd: z.number().positive() }).optional(),
    live: z
        .object({
        sessionId: z.string(),
        ws: z.string(),
        campaignId: z.string(),
        voice: VoiceSetting,
        hardExclusions: z.array(z.string()).default([]),
    })
        .optional(),
    playerKey: z.string().optional(),
});
/** ADR-114: the campaign-progress key of a pass — what ADR-088's continuation is filed under. */
export function passPlayerKey(token) {
    return `pass-${token}`;
}
/** What the pod says about its gate, so the client knows whether to ask for a pass. */
export const LobbyPolicy = z.object({ requirePass: z.boolean() });
export const UsageSummary = z.object({
    inputTokens: z.number().int().nonnegative(),
    cacheReadInputTokens: z.number().int().nonnegative(),
    cacheCreationInputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
});
export const CostLine = z.object({ usd: z.number().nonnegative() });
export const SessionCostSummary = z.object({
    narration: CostLine.extend({ model: z.string().nullable(), usage: UsageSummary }),
    reaction: CostLine.extend({ calls: z.number().int().nonnegative(), usage: UsageSummary }),
    assessor: CostLine.extend({ calls: z.number().int().nonnegative(), usage: UsageSummary }),
    tts: CostLine.extend({ chars: z.number().int().nonnegative() }),
    stt: CostLine.extend({ seconds: z.number().nonnegative() }),
    pod: CostLine.extend({ minutes: z.number().int().nonnegative() }),
    totalUsd: z.number().nonnegative(),
    rateCardVersion: z.string(),
});
export const AdminSession = z.object({
    sessionId: z.string(),
    state: SessionState,
    personaId: z.string().nullable(),
    passToken: z.string().nullable(),
    startedAt: z.string().datetime().nullable(),
    playedMinutes: z.number().int().nonnegative(),
    turns: z.number().int().nonnegative(),
    seq: z.number().int().nonnegative(),
    narratorModel: z.string().nullable(),
    usage: UsageSummary,
    /** Narration spend alone, kept for the first admin page; `cost.totalUsd` is the session's. */
    estimatedUsd: z.number().nonnegative(),
    rateCardVersion: z.string(),
    logPath: z.string().nullable(),
    /** Every line of the spend (§8.8): narration, side routes, voice, the pod's share. */
    cost: SessionCostSummary,
    profile: z.string(),
    passNote: z.string().nullable(),
    endedAt: z.string().datetime().nullable(),
    /** Written on every turn and at the end, so the history survives the pod. */
    updatedAt: z.string().datetime(),
    live: z.boolean(),
});
/* ------------------------------------------------------ playtest requests */
/**
 * The public site's "ask for a pass" form (2026-09-08). A request is not a pass: a
 * person reads it in the backoffice and issues one by hand, which is the point of a
 * controlled playtest. The 18+ confirmation is a literal `true` so a body without it
 * is refused at the schema, not by a check somebody could forget.
 */
export const PlaytestRequestBody = z.object({
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().email().max(254),
    timezone: z.string().trim().min(1).max(80),
    hasHeadphones: z.boolean(),
    hasMicrophone: z.boolean(),
    heardFrom: z.string().trim().max(500).optional(),
    adultConfirmed: z.literal(true),
});
export const PlaytestRequest = PlaytestRequestBody.extend({
    id: z.string(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=admin.js.map